import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

// workspace.pages 목록 fetch / 생성 / 수정 / 아카이브 훅.
// 조직(org) 단위로 archived=false 페이지 전체를 불러와 사이드바 트리에 사용한다.
export default function usePages() {
  const { user, membership } = useAuth();
  const orgId = membership?.org_id ?? null;

  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPages = useCallback(async () => {
    if (!orgId) {
      setPages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .schema('workspace')
      .from('pages')
      .select('*')
      .eq('org_id', orgId)
      .eq('archived', false)
      .neq('type', 'db_row')
      .order('position', { ascending: true });
    if (err) {
      // eslint-disable-next-line no-console
      console.error('[루트/nav] 페이지 목록 로드 실패', err);
      setError(err);
      setPages([]);
    } else {
      setPages(data || []);
    }
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  // 다른 화면(데이터베이스/에디터)에서 제목·아이콘을 바꾸면 사이드바를 즉시 갱신
  useEffect(() => {
    const h = () => fetchPages();
    window.addEventListener('rt:pages', h);
    return () => window.removeEventListener('rt:pages', h);
  }, [fetchPages]);

  const createPage = useCallback(
    async ({ parentId = null, teamspaceId = null, collectionId = null, title = '', type = 'document', icon = null } = {}) => {
      if (!orgId || !user?.id) return null;
      const row = {
        org_id: orgId,
        parent_id: parentId,
        teamspace_id: teamspaceId,
        type,
        title,
        position: Date.now(),
        created_by: user.id,
      };
      // collection_id 컬럼이 아직 없을 수도 있으므로(마이그레이션 전) 값이 있을 때만 포함.
      if (collectionId) row.collection_id = collectionId;
      // 템플릿에서 생성 시 아이콘 승계 — 값이 있을 때만 포함.
      if (icon) row.icon = icon;
      const { data, error: err } = await supabase
        .schema('workspace')
        .from('pages')
        .insert(row)
        .select('*')
        .single();
      if (err) {
        // eslint-disable-next-line no-console
        console.error('[루트/nav] 페이지 생성 실패', err);
        setError(err);
        return null;
      }
      await fetchPages();
      return data;
    },
    [orgId, user?.id, fetchPages]
  );

  const updatePage = useCallback(
    async (id, patch) => {
      const { error: err } = await supabase
        .schema('workspace')
        .from('pages')
        .update(patch)
        .eq('id', id);
      if (err) {
        // eslint-disable-next-line no-console
        console.error('[루트/nav] 페이지 수정 실패', err);
        setError(err);
        return false;
      }
      await fetchPages();
      return true;
    },
    [fetchPages]
  );

  const archivePage = useCallback(
    async (id) => {
      const { error: err } = await supabase
        .schema('workspace')
        .from('pages')
        .update({ archived: true })
        .eq('id', id);
      if (err) {
        // eslint-disable-next-line no-console
        console.error('[루트/nav] 페이지 아카이브 실패', err);
        setError(err);
        return false;
      }
      await fetchPages();
      return true;
    },
    [fetchPages]
  );

  return { pages, loading, error, fetchPages, createPage, updatePage, archivePage };
}

// teamspaceId 로 페이지를 필터링하는 헬퍼.
// teamspaceId === null 이면 어느 팀에도 속하지 않은(소속 없는) 페이지를 반환.
export function filterPagesByTeamspace(pages, teamspaceId) {
  if (teamspaceId === null) {
    return pages.filter((p) => !p.teamspace_id);
  }
  return pages.filter((p) => p.teamspace_id === teamspaceId);
}

// 특정 컬렉션(커스텀 섹션)에 속한, 소속 팀스페이스 없는 페이지를 반환.
// collection_id 컬럼이 없으면(마이그레이션 전) 항상 빈 배열.
export function filterPagesByCollection(pages, collectionId) {
  if (!collectionId) return [];
  return pages.filter((p) => !p.teamspace_id && p.collection_id === collectionId);
}

// 어떤 컬렉션에도 속하지 않은(collection_id 없는) 페이지인지.
// 컬럼이 없으면 collection_id 가 undefined → 항상 true (기존 분류 동작 유지).
export function hasNoCollection(page) {
  return page.collection_id === null || page.collection_id === undefined;
}
